import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "../../lib/adminApi";
import ImageUpload from "../../components/ImageUpload";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function countWords(s) {
  return (s || "").trim().split(/\s+/).filter(Boolean).length;
}

const EMPTY = {
  category: "residencias",
  name: "",
  number: "01",
  location: "Barú, Colombia",
  hero: "",
  summary: "",
  description: "",
  thumbs: [],
  order: 0,
};

export default function AdminProjectForm() {
  const { id } = useParams();
  const isEdit = id && id !== "nuevo";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await adminApi.get(`/projects/${id}`);
        setForm({
          category: data.category,
          name: data.name,
          number: data.number,
          location: data.location,
          hero: data.hero,
          summary: data.summary || "",
          description: data.description || "",
          thumbs: data.thumbs || [],
          order: data.order || 0,
        });
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/admin/proyectos");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addThumb = () =>
    set({ thumbs: [...form.thumbs, { label: "Detalle", image: "" }] });
  const updateThumb = (i, patch) =>
    set({ thumbs: form.thumbs.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) });
  const removeThumb = (i) =>
    set({ thumbs: form.thumbs.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.hero) return toast.error("La imagen principal (hero) es obligatoria.");
    setBusy(true);
    try {
      if (isEdit) {
        await adminApi.put(`/projects/${id}`, form);
        toast.success("Proyecto actualizado");
      } else {
        await adminApi.post("/projects", form);
        toast.success("Proyecto creado");
      }
      navigate("/admin/proyectos");
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="text-[var(--pc-bronze)] animate-spin" />
      </div>
    );
  }

  return (
    <section className="px-10 py-12 max-w-[920px]" data-testid="admin-project-form">
      <button
        onClick={() => navigate("/admin/proyectos")}
        className="flex items-center gap-2 text-[var(--pc-text-soft)] hover:text-[var(--pc-text)] mb-6"
        style={{ fontFamily: "Inter", fontWeight: 400, fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase" }}
        data-testid="back-to-projects"
      >
        <ArrowLeft size={14} /> Volver
      </button>

      <h1
        className="headline-xl text-[var(--pc-text)]"
        style={{ fontSize: "clamp(24px, 2.4vw, 34px)", letterSpacing: "0.16em", lineHeight: 1.2 }}
      >
        {isEdit ? "Editar proyecto" : "Nuevo proyecto"}
      </h1>
      <div className="h-px w-10 bg-[var(--pc-bronze)] mt-5 opacity-90" />

      <form onSubmit={submit} className="mt-10 space-y-7">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="admin-label">Categoría</label>
            <select
              value={form.category}
              onChange={(e) => set({ category: e.target.value })}
              className="admin-input w-full"
              data-testid="field-category"
            >
              <option value="residencias">Residencias</option>
              <option value="enclaves">Enclaves</option>
            </select>
          </div>
          <div>
            <label className="admin-label">Número</label>
            <input
              type="text"
              value={form.number}
              onChange={(e) => set({ number: e.target.value })}
              className="admin-input w-full"
              maxLength={4}
              required
              data-testid="field-number"
            />
          </div>
          <div>
            <label className="admin-label">Orden</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => set({ order: parseInt(e.target.value || "0", 10) })}
              className="admin-input w-full"
              data-testid="field-order"
            />
          </div>
        </div>

        <div>
          <label className="admin-label">Nombre del proyecto</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            className="admin-input w-full"
            required
            data-testid="field-name"
          />
        </div>

        <div>
          <label className="admin-label">Ubicación</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => set({ location: e.target.value })}
            className="admin-input w-full"
            required
            data-testid="field-location"
          />
        </div>

        <div>
          <label className="admin-label">Sumario (frase corta · máx ~120 caracteres)</label>
          <input
            type="text"
            value={form.summary}
            onChange={(e) => set({ summary: e.target.value })}
            className="admin-input w-full"
            maxLength={200}
            placeholder="Ej: Casa de techo a dos aguas en Barú"
            data-testid="field-summary"
          />
        </div>

        <div>
          <label className="admin-label">
            Descripción (50–70 palabras)
            <span className="ml-3 text-[var(--pc-text-mute)]" style={{ letterSpacing: 0 }}>
              {`· ${countWords(form.description)} palabras`}
            </span>
          </label>
          <textarea
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
            className="admin-input w-full"
            rows={5}
            maxLength={2000}
            placeholder="Describe el proyecto: lugar, materialidad, intención y forma de habitar..."
            data-testid="field-description"
          />
        </div>

        <ImageUpload
          value={form.hero}
          onChange={(v) => set({ hero: v })}
          label="Imagen principal (hero)"
          testId="field-hero"
        />

        <div className="pt-4">
          <div className="flex items-center justify-between mb-4">
            <label className="admin-label !mb-0">Galería (miniaturas)</label>
            <button
              type="button"
              onClick={addThumb}
              className="admin-btn-ghost flex items-center gap-2 text-xs"
              data-testid="add-thumb"
            >
              <Plus size={12} /> Añadir
            </button>
          </div>
          {form.thumbs.length === 0 && (
            <p className="text-[var(--pc-text-soft)] text-xs">Sin miniaturas. Añade hasta 4 imágenes adicionales.</p>
          )}
          <div className="space-y-5">
            {form.thumbs.map((t, i) => (
              <div key={i} className="border border-[var(--pc-line)] p-5 bg-[#0b0b0d]" data-testid={`thumb-row-${i}`}>
                <div className="flex justify-between items-center mb-3">
                  <span
                    className="text-[var(--pc-text-soft)]"
                    style={{ fontFamily: "Inter", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase" }}
                  >
                    Imagen {i + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeThumb(i)}
                    className="text-red-300 hover:text-red-200 flex items-center gap-1 text-xs"
                    data-testid={`remove-thumb-${i}`}
                  >
                    <Trash2 size={12} /> Quitar
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="admin-label">Etiqueta</label>
                    <input
                      type="text"
                      value={t.label}
                      onChange={(e) => updateThumb(i, { label: e.target.value })}
                      className="admin-input w-full"
                      data-testid={`thumb-label-${i}`}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <ImageUpload
                      value={t.image}
                      onChange={(v) => updateThumb(i, { image: v })}
                      label="Imagen"
                      testId={`thumb-image-${i}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-6 flex gap-3">
          <button type="submit" disabled={busy} className="admin-btn-primary" data-testid="save-project">
            {busy ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear proyecto"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/proyectos")}
            className="admin-btn-ghost"
            data-testid="cancel-project"
          >
            Cancelar
          </button>
        </div>
      </form>
    </section>
  );
}
